<template>
  <v-container fluid class="vip-profile-container">
    <v-card class="vip-profile-card">
      <v-card-title class="profile-card-header text-white text-h5 text-center font-weight-bold">
        Edit VIP Profile
      </v-card-title>
      <v-divider></v-divider>
      <template v-if="vip">
        <v-form @submit.prevent="saveProfile" class="pa-5">
          <v-row>
            <v-col cols="12" md="6">
              <!-- Non-editable fields -->
              <v-text-field label="Username" v-model="vip.user.username" disabled />
              <!-- Display formatted birthday (dd-mm-yyyy) but keep the original for submission -->
              <v-text-field label="Birthday" v-model="tempBirthday" disabled />
              <!-- Phone: country code dropdown (with custom selection) and local number -->
              <v-row dense>
                <v-col cols="4">
                  <v-select
                    :items="europeCodes"
                    v-model="tempPhoneCountry"
                    label="Country Code"
                    item-title="label"
                    item-value="value"
                    hide-details
                  >
                    <template #selection="data">
                      <span>{{ data.item.value }}</span>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="8">
                  <v-text-field v-model="tempPhoneLocal" label="Local Phone Number"></v-text-field>
                </v-col>
              </v-row>
              <v-text-field
                label="Email"
                v-model="vip.user.email"
                :error="!!errors.email"
                :error-messages="errors.email"
              />
            </v-col>
            <v-col cols="12" md="6" class="text-center">
              <v-avatar size="140">
                <img
                  :key="previewImageSrc"
                  :src="previewImageSrc"
                  alt="Profile Picture"
                  class="profile-img"
                  :style="profileImgStyle"
                  @error="useDefaultProfilePicture"
                />
              </v-avatar>
              <div class="mt-4">
                <v-btn color="primary" @click="triggerFileSelect">
                  Choose File
                </v-btn>
                <div class="text-caption grey--text mt-2">Max file size: 10 MB.</div>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  class="hidden-file-input"
                  @change="onFileChange"
                />
              </div>
            </v-col>
          </v-row>
          <v-divider class="my-5"></v-divider>
          <v-row justify="center">
            <v-col cols="auto">
              <v-btn
                color="success"
                type="submit"
                :loading="isSaving"
                :disabled="isSaving"
              >
                Save
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn color="error" outlined @click="cancelEdit">Cancel</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </template>
      <v-card-text v-else>
        <p>Loading or error: No VIP data found.</p>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";
export default {
  name: "EditVIPProfile",
  data() {
    return {
      vip: null,
      // For display purposes (dd-mm-yyyy)
      tempBirthday: "",
      // Keep the original birthday (yyyy-mm-dd) for submission
      originalBirthday: "",
      tempProfilePicture: null,
      previewUrl: null,
      defaultProfilePicture: "/media/profile_pictures/default_profile.png",
      // Phone split: country code and local number
      tempPhoneCountry: "",
      tempPhoneLocal: "",
      europeCodes: [
        { value: "+30", label: "+30 Greece" },
        { value: "+31", label: "+31 Netherlands" },
        { value: "+32", label: "+32 Belgium" },
        { value: "+33", label: "+33 France" },
        { value: "+34", label: "+34 Spain" },
        { value: "+36", label: "+36 Hungary" },
        { value: "+39", label: "+39 Italy" },
        { value: "+40", label: "+40 Romania" },
        { value: "+41", label: "+41 Switzerland" },
        { value: "+43", label: "+43 Austria" },
        { value: "+44", label: "+44 United Kingdom" },
        { value: "+45", label: "+45 Denmark" },
        { value: "+46", label: "+46 Sweden" },
        { value: "+47", label: "+47 Norway" },
        { value: "+48", label: "+48 Poland" },
        { value: "+49", label: "+49 Germany" },
        { value: "+351", label: "+351 Portugal" },
        { value: "+353", label: "+353 Ireland" },
        { value: "+354", label: "+354 Iceland" },
        { value: "+355", label: "+355 Albania" },
        { value: "+356", label: "+356 Malta" },
        { value: "+357", label: "+357 Cyprus" },
        { value: "+358", label: "+358 Finland" },
        { value: "+370", label: "+370 Lithuania" },
        { value: "+371", label: "+371 Latvia" },
        { value: "+372", label: "+372 Estonia" },
        { value: "+386", label: "+386 Slovenia" },
      ],
      errors: {},
      isSaving: false,
    };
  },
  computed: {
    currentProfileImage() {
      return this.vip && this.vip.profile_picture_url
        ? this.vip.profile_picture_url
        : this.defaultProfilePicture;
    },
    previewImageSrc() {
      return this.previewUrl || this.currentProfileImage;
    },
    profileImgStyle() {
      return { border: "3px solid #C0C0C0" };
    },
    cooldownActive() {
      if (!this.vip || !this.vip.last_profile_picture_update) return false;
      const lastUpdate = new Date(this.vip.last_profile_picture_update);
      const now = new Date();
      const cooldownPeriod = 7 * 24 * 60 * 60 * 1000;
      return now.getTime() < lastUpdate.getTime() + cooldownPeriod;
    },
    nextUploadAllowed() {
      if (!this.vip || !this.vip.last_profile_picture_update) return null;
      const lastUpdate = new Date(this.vip.last_profile_picture_update);
      return new Date(lastUpdate.getTime() + 7 * 24 * 60 * 60 * 1000);
    },
    formattedNextUploadAllowed() {
      return this.nextUploadAllowed ? this.nextUploadAllowed.toLocaleString() : "";
    },
  },
  methods: {
    async fetchVIPProfile() {
      try {
        const res = await axios.get("/api/vip-profile/", { withCredentials: true });
        this.vip = res.data;
        // Save the original birthday (assumed to be in yyyy-mm-dd)
        this.originalBirthday = this.vip.birthday || "";
        // Format birthday for display (dd-mm-yyyy)
        this.tempBirthday = this.vip.birthday ? this.formatBirthday(this.vip.birthday) : "N/A";
        // Split the stored phone into country code and local part
        if (this.vip.phone) {
          let found = false;
          for (const code of this.europeCodes) {
            if (this.vip.phone.startsWith(code.value)) {
              this.tempPhoneCountry = code.value;
              this.tempPhoneLocal = this.vip.phone.substring(code.value.length).trim();
              found = true;
              break;
            }
          }
          if (!found) {
            this.tempPhoneCountry = "";
            this.tempPhoneLocal = this.vip.phone;
          }
        } else {
          this.tempPhoneCountry = "";
          this.tempPhoneLocal = "";
        }
      } catch (err) {
        console.error("Error fetching VIP profile:", err);
        this.vip = null;
      }
    },
    // Format birthday for display: from "YYYY-MM-DD" to "DD-MM-YYYY"
    formatBirthday(date) {
      if (!date) return "N/A";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },
    useDefaultProfilePicture(e) {
      e.target.src = this.defaultProfilePicture;
    },
    triggerFileSelect() {
      if (this.cooldownActive) {
        alert(
          `You can only change your profile picture once per week. You can change it again on: ${this.formattedNextUploadAllowed}`
        );
        return;
      }
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const files = e.target.files;
      if (!files || !files.length) return;

      const file = files[0];

      // 10 MB limit
      const MAX_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        alert('Profile picture upload failed: file size exceeds 10 MB.');
        e.target.value = '';
        return;
      }

      if (this.cooldownActive) {
        e.target.value = '';
        alert(
          `You can only change your profile picture once per week. ` +
          `You can change it again on: ${this.formattedNextUploadAllowed}`
        );
        return;
      }

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }

      this.tempProfilePicture = file;
      this.previewUrl = URL.createObjectURL(file);
    },
    async saveProfile() {
      this.errors = {};
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        const formData = new FormData();
        formData.append("email", this.vip.user.email);
        // Combine the country code and local phone number
        const combinedPhone = (this.tempPhoneCountry + " " + this.tempPhoneLocal).trim();
        formData.append("phone", combinedPhone);
        // Send the original birthday (in yyyy-mm-dd) for backend validation
        formData.append("birthday", this.originalBirthday);
        if (this.tempProfilePicture) {
          formData.append("profile_picture", this.tempProfilePicture);
        }
        await axios.patch("/api/vip-profile/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
          this.previewUrl = null;
        }
        this.tempProfilePicture = null;
        this.$router.push({ name: "CustomerProfile" });
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors = error.response.data;
        } else {
          console.error("Error saving VIP profile:", error);
        }
      } finally {
        this.isSaving = false;
      }
    },
    cancelEdit() {
      this.$router.push({ name: "CustomerProfile" });
    },
  },
  async created() {
    await this.fetchVIPProfile();
  },
  unmounted() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  },
};
</script>

<style scoped>
.vip-profile-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  min-height: 100vh;
  padding: 30px 20px;
}
.vip-profile-card {
  max-width: 620px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}
.profile-card-header {
  background-color: #00321E;
  font-size: 18px;
  font-weight: bold;
  padding: 10px;
  color: #000;
}
.profile-img {
  width: 140px;
  height: 140px;
  object-fit: cover;
  border-radius: 50%;
}
.hidden-file-input {
  display: none;
}
</style>
